import React from "react";
import { Message, DraftPost, UIAction, GeneratedPostsData, BlogPost, OutreachEmailDraft, ScannedCardDetails, GbpMessageActions } from "../types";
import { MarkdownRenderer } from "./MarkdownRenderer";
import { ActionButtons, ActionClickContext } from "./ActionButtons";
import { PromptCard } from "./PromptCard";
import { GeneratedImages } from "./GeneratedImages";
import { GeneratedPosts } from "./GeneratedPosts";
import { BlogReadyCard } from "./BlogReadyCard";
import { OutreachEmailCard } from "./OutreachEmailCard";
import { OutreachCampaignFlow } from "./OutreachCampaignFlow";
import { GeneratedVideos } from "./GeneratedVideos";
import { MessageActions } from "../types";
import { VideoPreviewPlayer } from "../../../../features/ugc-video/components/VideoPreviewPlayer";
import { ContactReviewCard } from "./ContactReviewCard";
import { ContactsListCard } from "./ContactsListCard";
import { CallsListCard } from "./CallsListCard";
import { AvailabilityCard } from "./AvailabilityCard";
import { AppointmentCard } from "./AppointmentCard";
import { SendDraftCard } from "./SendDraftCard";
import { ScannedCardsCard } from "./ScannedCardsCard";
import { ScannedCardsPending } from "./ScannedCardsPending";
import { CardDetailsReview } from "./CardDetailsReview";
import { recheckCards } from "../../../../features/card-to-crm/receptionistCardService";
import { ContactResultCard } from "./ContactResultCard";
import { ConversationCard } from "./ConversationCard";
import { InboxCard } from "./InboxCard";
import type { PatchableContactFields } from "../../../../services/contactImportService";
import {
  CHAT_ASSISTANT_CONTENT_CLASS,
  CHAT_COLUMN_CLASS,
  CHAT_USER_BUBBLE_CLASS,
} from "../constants/chatLayout";
import { injectCitationLinks } from "../utils/injectCitationLinks";
import { CHAT_MODEL_OPTIONS } from "../config/modelOptions";
import { GbpFixProposal } from "./GbpFixProposal";
import { GbpPlanCard } from "./GbpPlanCard";

/**
 * Display label for a model id answering a turn (SGL-368), e.g. "claude-sonnet-5" -> "Sonnet 5".
 * Bedrock-routed models arrive as the full inference-profile id (e.g.
 * "us.anthropic.claude-opus-5", see BEDROCK_MODEL_IDS in bedrock_adapter.py), not the bare alias
 * stored in CHAT_MODEL_OPTIONS, so an exact match alone would miss every Claude reply — fall back
 * to a substring match before giving up and showing the raw id.
 */
const answeringModelLabel = (modelId: string | undefined): string | undefined => {
  if (!modelId) return undefined;
  const option = CHAT_MODEL_OPTIONS.find((o) => o.model === modelId)
    ?? CHAT_MODEL_OPTIONS.find((o) => modelId.includes(o.model));
  return option?.label ?? modelId;
};

interface MessageBubbleProps {
  message: Message;
  onPublish?: (postData: MessageActions["post_data"]) => void;
  onSchedule?: (postData: MessageActions["post_data"]) => void;
  onPromptSelect?: (promptText: string) => void;
  onSavePosts?: (selectedPosts: DraftPost[]) => void;
  onSaveVideos?: (selectedVideos: any[]) => void;
  onActionClick?: (action: UIAction, context?: ActionClickContext) => void;
  isPublishing?: boolean;
  isScheduling?: boolean;
  isSavingPosts?: boolean;
  isSavingVideos?: boolean;
  isLoading?: boolean;
  /** When true, do not render generated posts here (they are shown only in the right panel). */
  hideEmbeddedPosts?: boolean;
  /** When posts are hidden, call this to open the right panel with these posts. */
  onOpenPostsPanel?: (data: GeneratedPostsData) => void;
  /** Attach an image from the media library to a draft post by temp_id. */
  onInsertImageForPost?: (tempId: string) => void | Promise<void>;
  /** Open the right-panel canvas for a specific post from this message. */
  onOpenPostCanvas?: (post: DraftPost, allPosts: import("../types").GeneratedPostsData) => void;
  /** Open the right-panel blog editor (canvas) for this message's blog post. */
  onOpenBlogCanvas?: (blog: BlogPost) => void;
  /** Applies an edit (subject/body) or Approve to this message's outreach email draft. */
  onOutreachEmailChange?: (message: Message, next: OutreachEmailDraft) => void;
  /** Sends a new chat message asking the model to redraft the outreach email. */
  onOutreachEmailRegenerate?: () => void;
  /** Tenant the thread belongs to — the outreach campaign flow calls the contact
   *  and send APIs directly, so it needs both this and the caller's token.
   *  SendDraftCard needs it too (SGL-244): without it the card renders
   *  read-only rather than offering an approve it can't honour. */
  customerId?: string;
  idToken?: string;
  /** Persists an inline edit on this message's contact batch (SGL-81). */
  onPatchContact?: (message: Message, index: number, fields: PatchableContactFields) => Promise<void>;
  onWriteCardIntros?: (message: Message, contacts: ScannedCardDetails[], brief: string, captureContext: string) => Promise<void>;
  /** A correction on the scan, recorded before the owner asks the agent anything. */
  onCardDetailsEdited?: (message: Message, contacts: ScannedCardDetails[], captureContext: string) => void;
  /** SGL-328: what the Business Profile card can ask the chat window to do. */
  gbpActions?: GbpMessageActions;
  /** Upserts the selected contacts from this message's batch into GHL (SGL-81). */
  onImportContactsFromMessage?: (message: Message, selectedIndices: number[], tags: string[]) => Promise<void>;
  isImportingContacts?: boolean;
  contactUpsertError?: string | null;
  /** Fetch the next page for this message's contacts list (SGL-81 "ask my contacts"). */
  onLoadMoreContacts?: (message: Message) => Promise<void>;
  /** Runs a filtered search, replacing this message's contacts list (SGL-81). */
  onSearchContacts?: (message: Message, query: string) => Promise<void>;
}

const MessageBubbleComponent: React.FC<MessageBubbleProps> = ({
  message,
  onPublish,
  onSchedule,
  onPromptSelect,
  onSavePosts,
  onSaveVideos,
  onActionClick,
  isPublishing = false,
  isScheduling = false,
  isSavingPosts = false,
  isSavingVideos = false,
  isLoading = false,
  hideEmbeddedPosts = false,
  onOpenPostsPanel,
  onInsertImageForPost,
  onOpenPostCanvas,
  onOpenBlogCanvas,
  onOutreachEmailChange,
  onOutreachEmailRegenerate,
  customerId,
  idToken,
  onPatchContact,
  onWriteCardIntros,
  onCardDetailsEdited,
  gbpActions,
  onImportContactsFromMessage,
  isImportingContacts = false,
  contactUpsertError,
  onLoadMoreContacts,
  onSearchContacts,
}) => {
  return (
    <div className={CHAT_COLUMN_CLASS}>
      <div
        className={`${
          message.role === "user" ? "flex justify-end" : ""
        } w-full min-w-0`}
      >
        {message.role === "user" ? (
          <div
            className={`p-4 rounded-2xl border border-theme-border bg-theme-surface text-theme-fg ${CHAT_USER_BUBBLE_CLASS}`}
          >
            <p className="text-theme-fg leading-relaxed whitespace-pre-wrap break-words">
              {message.content}
            </p>
            {message.images && message.images.length > 0 && (
              <div className="mt-3 flex flex-col gap-2">
                {/* Images */}
                {(() => {
                  const imgs = message.images!.filter(
                    (img) =>
                      img.media_type === "IMAGE" ||
                      (img.media_type || "").startsWith("image/") ||
                      (img.cloudfront_url || "").startsWith("data:image")
                  );
                  const docs = message.images!.filter(
                    (img) =>
                      img.media_type !== "IMAGE" &&
                      !(img.media_type || "").startsWith("image/") &&
                      !(img.cloudfront_url || "").startsWith("data:image")
                  );

                  const ext = (filename: string) =>
                    filename.split(".").pop()?.toLowerCase() ?? "";

                  const docIcon = (filename: string) => {
                    const e = ext(filename);
                    if (e === "pdf") return { icon: "picture_as_pdf", color: "text-red-500", bg: "bg-red-50" };
                    if (e === "docx" || e === "doc") return { icon: "description", color: "text-blue-500", bg: "bg-blue-50" };
                    if (e === "json") return { icon: "data_object", color: "text-yellow-600", bg: "bg-yellow-50" };
                    if (e === "csv") return { icon: "table_chart", color: "text-green-600", bg: "bg-green-50" };
                    if (e === "md") return { icon: "article", color: "text-purple-500", bg: "bg-purple-50" };
                    return { icon: "insert_drive_file", color: "text-theme-fg-muted", bg: "bg-theme-background" };
                  };

                  return (
                    <>
                      {imgs.length > 0 && (
                        <div className={imgs.length === 1 ? "" : "grid grid-cols-2 gap-1.5"}>
                          {imgs.map((img) => (
                            <div
                              key={img.media_id}
                              className={`overflow-hidden rounded-xl border border-theme-border bg-theme-background ${
                                imgs.length === 1 ? "max-h-64" : "aspect-square"
                              }`}
                            >
                              <img
                                src={img.cloudfront_url}
                                alt={img.s3_key}
                                className={`w-full h-full ${imgs.length === 1 ? "object-contain" : "object-cover"}`}
                              />
                            </div>
                          ))}
                        </div>
                      )}
                      {docs.length > 0 && (
                        <div className="flex flex-col gap-1.5">
                          {docs.map((doc) => {
                            const { icon, color, bg } = docIcon(doc.s3_key);
                            return (
                              <div
                                key={doc.media_id}
                                className={`flex items-center gap-2.5 rounded-xl border border-theme-border px-3 py-2.5 ${bg}`}
                              >
                                <span className={`material-icons-outlined text-[22px] shrink-0 ${color}`}>{icon}</span>
                                <div className="min-w-0">
                                  <p className="truncate text-[13px] font-medium text-theme-fg leading-tight">{doc.s3_key}</p>
                                  <p className="text-[11px] text-theme-fg-muted uppercase tracking-wide mt-0.5">{ext(doc.s3_key) || "file"}</p>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </>
                  );
                })()}
              </div>
            )}
          </div>
        ) : message.type === "action" ? (
          <div className={CHAT_ASSISTANT_CONTENT_CLASS}>
            <div className="inline-flex items-center gap-2 rounded-full border border-theme-border bg-theme-surface-ui px-3 py-2 text-sm text-theme-fg">
              <span className="inline-block h-2 w-2 rounded-full bg-theme-fg-muted" />
              <span>{message.content || "Working..."}</span>
            </div>
          </div>
        ) : message.type === "action_result" && message.image_urls && message.image_urls.length > 0 ? (
          <div className={CHAT_ASSISTANT_CONTENT_CLASS}>
            {message.content && (
              <p className="mb-2 text-sm text-theme-fg-muted">{message.content}</p>
            )}
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mt-2">
              {message.image_urls.map((url, i) => (
                <a
                  key={i}
                  href={url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block rounded-lg overflow-hidden border border-theme-border hover:border-theme-primary transition-colors"
                >
                  <img src={url} alt="" className="w-full aspect-square object-cover" />
                </a>
              ))}
            </div>
          </div>
        ) : (
          <>
            <div className={CHAT_ASSISTANT_CONTENT_CLASS}>
              <MarkdownRenderer content={injectCitationLinks(message.content, message.citations)} />
              {(message.actions || message.uiActions) && (onPublish || onSchedule || onActionClick) && (
                <ActionButtons
                  actions={message.actions}
                  uiActions={message.uiActions}
                  onPublish={onPublish}
                  onSchedule={onSchedule}
                  onActionClick={onActionClick ? (action) => onActionClick(action, { generatedPosts: message.generatedPosts }) : undefined}
                  isPublishing={isPublishing}
                  isScheduling={isScheduling}
                  isLoading={isLoading}
                />
              )}
              {message.promptOptions &&
                message.promptOptions.length > 0 &&
                onPromptSelect && (
                  <div className="mt-4">
                    {message.promptOptions.map((prompt) => (
                      <PromptCard
                        key={prompt.number}
                        promptNumber={prompt.number}
                        promptTitle={prompt.text}
                        promptText={prompt.fullPrompt || prompt.text}
                        onSelect={onPromptSelect}
                        isDisabled={isLoading}
                      />
                    ))}
                  </div>
                )}
              {message.generatedImages && (
                <GeneratedImages
                  imageUrls={message.generatedImages.urls}
                  prompt={message.generatedImages.prompt}
                  provider={message.generatedImages.provider}
                  customerId={customerId}
                  idToken={idToken}
                />
              )}
              {message.generatedPosts && hideEmbeddedPosts && onOpenPostsPanel && (
                <button
                  type="button"
                  onClick={() => onOpenPostsPanel(message.generatedPosts!)}
                  className="mt-2 inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-sm font-medium text-theme-primary transition-colors hover:bg-theme-secondary/20 hover:text-theme-primary"
                >
                  <span className="material-icons text-lg">open_in_new</span>
                  View posts
                </button>
              )}
              {message.generatedVideos && onSaveVideos && (
                <GeneratedVideos
                  videos={message.generatedVideos.videos}
                  onSave={onSaveVideos}
                  isSaving={isSavingVideos}
                />
              )}
              {message.contactBatch && onPatchContact && onImportContactsFromMessage && (
                <ContactReviewCard
                  batch={message.contactBatch}
                  onPatchContact={(index, fields) => onPatchContact(message, index, fields)}
                  onImport={(selectedIndices, tags) => onImportContactsFromMessage(message, selectedIndices, tags)}
                  isImporting={isImportingContacts}
                  importError={contactUpsertError}
                />
              )}
              {message.contactsList && onLoadMoreContacts && onSearchContacts && (
                <ContactsListCard
                  page={message.contactsList}
                  onLoadMore={() => onLoadMoreContacts(message)}
                  onSearch={(query) => onSearchContacts(message, query)}
                />
              )}
              {message.ugcVideo && message.ugcVideo.videoUrl && (
                <div className="mt-4">
                  <VideoPreviewPlayer
                    videoUrl={message.ugcVideo.videoUrl}
                    videoId={message.ugcVideo.videoId}
                  />
                </div>
              )}
            </div>
            {message.generatedPosts && onSavePosts && !hideEmbeddedPosts && (
              <div className="mt-4 w-full min-w-0 max-w-full">
                <GeneratedPosts
                  posts={message.generatedPosts.posts}
                  onSave={onSavePosts}
                  isSaving={isSavingPosts}
                  onInsertImage={onInsertImageForPost}
                  onOpenCanvas={
                    onOpenPostCanvas && message.generatedPosts
                      ? (post) => onOpenPostCanvas(post, message.generatedPosts!)
                      : undefined
                  }
                />
              </div>
            )}
            {message.blogPost && onOpenBlogCanvas && (
              <div className="mt-4 w-full min-w-0 max-w-full">
                <BlogReadyCard
                  blog={message.blogPost}
                  onOpenCanvas={() => onOpenBlogCanvas(message.blogPost!)}
                />
              </div>
            )}
            {message.outreachEmail && (
              <OutreachEmailCard
                draft={message.outreachEmail}
                isLoading={isLoading}
                onChange={
                  onOutreachEmailChange
                    ? (next) => onOutreachEmailChange(message, next)
                    : undefined
                }
                onRegenerate={onOutreachEmailRegenerate}
              />
            )}
            {message.outreachEmail?.approved && customerId && idToken && (
              <OutreachCampaignFlow
                draft={message.outreachEmail}
                customerId={customerId}
                idToken={idToken}
                onCampaignStarted={
                  onActionClick
                    ? (campaignId, total) =>
                        // Not a chat message: a raw campaign UUID in the transcript is
                        // noise to the person reading it. The id rides along on the next
                        // request instead, where only the model sees it.
                        onActionClick({
                          type: "campaign_started",
                          payload: { campaign_id: campaignId, total },
                        })
                    : undefined
                }
              />
            )}
            {message.calls && <CallsListCard data={message.calls} />}
            {/* Needs the tenant — without it the card would render a confirm
                button that can only fail (SGL-346). The token is resolved by
                authedFetch inside the save call. */}
            {message.cardContacts && customerId && (
              <ScannedCardsCard
                data={message.cardContacts}
                customerId={customerId}
                idToken={idToken || ""}
              />
            )}
            {message.cardContactsPending && (
              <ScannedCardsPending count={message.cardContactsPending.count} />
            )}
            {message.cardDetails && onWriteCardIntros && (
              <CardDetailsReview
                data={message.cardDetails}
                onWriteIntros={(contacts, brief, captureContext) =>
                  onWriteCardIntros(message, contacts, brief, captureContext)
                }
                onEdited={
                  onCardDetailsEdited &&
                  ((contacts, captureContext) =>
                    onCardDetailsEdited(message, contacts, captureContext))
                }
                onRecheck={
                  customerId && idToken
                    ? (contacts) => recheckCards(customerId, contacts, idToken)
                    : undefined
                }
              />
            )}
            {message.gbpProposal && (message.gbpProposal.plan !== undefined || message.gbpProposal.planError) && (
              <GbpPlanCard
                proposal={message.gbpProposal}
                onChange={gbpActions ? (next) => gbpActions.onChange(message, next) : undefined}
                onApply={gbpActions ? (items) => gbpActions.onApply(message, items) : undefined}
                onHandOff={gbpActions?.onHandOff}
                searchCategories={gbpActions ? (q) => gbpActions.searchCategories(message, q) : undefined}
                loadMediaLibrary={gbpActions?.loadMediaLibrary}
                onConnect={gbpActions?.onConnect}
                checkStatus={gbpActions?.checkStatus ? (all) => gbpActions.checkStatus!(message, all) : undefined}
                onAsk={gbpActions?.onAsk}
              />
            )}
            {message.gbpProposal && message.gbpProposal.plan === undefined && !message.gbpProposal.planError && (
              <GbpFixProposal
                proposal={message.gbpProposal}
                embedded
                onChange={gbpActions ? (next) => gbpActions.onChange(message, next) : undefined}
                onApply={gbpActions ? (items) => gbpActions.onApply(message, items) : undefined}
                onHandOff={gbpActions?.onHandOff}
                searchCategories={gbpActions ? (q) => gbpActions.searchCategories(message, q) : undefined}
                loadMediaLibrary={gbpActions?.loadMediaLibrary}
                onConnect={gbpActions?.onConnect}
                checkStatus={gbpActions?.checkStatus ? (all) => gbpActions.checkStatus!(message, all) : undefined}
              />
            )}
            {message.availability && <AvailabilityCard data={message.availability} />}
            {message.sendDraft && customerId && (
              <SendDraftCard draft={message.sendDraft.draft} customerId={customerId} />
            )}
            {message.booking && <AppointmentCard data={message.booking} />}
            {message.ghlContacts && <ContactResultCard data={message.ghlContacts} />}
            {message.ghlConversation && <ConversationCard data={message.ghlConversation} />}
            {message.ghlInbox && <InboxCard data={message.ghlInbox} />}
            {message.answeringModel && (
              <p className="mt-1 text-xs text-theme-fg-muted">
                Answered by {answeringModelLabel(message.answeringModel)}
              </p>
            )}
          </>
        )}
      </div>
    </div>
  );
};

// Callback props (onPublish, onSchedule, onActionClick, etc.) are recreated on
// every parent render, so comparing them would defeat memoization entirely.
// They're stable in *behavior* (always read current app state when invoked),
// so it's safe to ignore their identity here and compare only what actually
// changes a bubble's rendered output. This is what lets a live streaming
// update skip re-rendering every historical message bubble.
const arePropsEqual = (prev: MessageBubbleProps, next: MessageBubbleProps) =>
  prev.message === next.message &&
  prev.isPublishing === next.isPublishing &&
  prev.isScheduling === next.isScheduling &&
  prev.isSavingPosts === next.isSavingPosts &&
  prev.isSavingVideos === next.isSavingVideos &&
  prev.isLoading === next.isLoading &&
  prev.hideEmbeddedPosts === next.hideEmbeddedPosts &&
  // Same bug as NewChatThreadView.tsx's messageHistoryPropsEqual, one layer
  // deeper: this boundary ALSO gated on a fixed prop list that never
  // included the contacts-import loading state, so fixing the outer
  // boundary alone wasn't enough — this inner one still blocked it.
  prev.isImportingContacts === next.isImportingContacts &&
  prev.contactUpsertError === next.contactUpsertError;

export const MessageBubble = React.memo(MessageBubbleComponent, arePropsEqual);
